import React from 'react';
import {
  Activity, AlertCircle, AlertTriangle, Archive, ArrowLeft, ArrowRight, ArrowUpDown, ArrowUpRight,
  BadgeCheck, Banknote, BarChart2, Bell, BellOff, Building2,
  Calendar, CalendarCheck, Check, CheckCircle, CheckCircle2,
  ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ChevronsDown, ChevronsUp,
  Circle, CircleCheck, CircleHelp, ClipboardList, Clock, Code2, Copy,
  Database, Dot, Download,
  Eye, EyeOff, ExternalLink,
  FileText, FileType2, FlaskConical,
  Handshake, Hash, Home,
  Info,
  FolderOpen,
  KeyRound,
  Landmark, Layers, LayoutDashboard, Lightbulb, LineChart, Link2, ListFilter, Lock, LogOut, Unlock,
  Mail, MapPin, Maximize2, Megaphone, Menu,
  Pencil, PiggyBank, Play, Plus,
  RefreshCw, Route,
  ScrollText, Search, SearchCheck, ShieldAlert, ShieldCheck, SlidersHorizontal, Slash, Square, SquareCheck, Star,
  Terminal, TrendingDown, TrendingUp, Trash2,
  Upload,
  User, UserPen, UserPlus, UserX, Users,
  Wallet, Wheat,
  X, XCircle,
} from 'lucide-react';

// Registro explícito dos ícones usados no portal — em vez de `import * as Icons` +
// acesso dinâmico (`Icons[nome]`), que impede o bundler de eliminar os ~1650 ícones
// não usados do lucide-react (import * não é tree-shakeable mesmo com sideEffects
// false, porque o acesso por propriedade dinâmica esconde do bundler quais exports
// realmente são lidos). Adicionar um ícone novo: importe o componente acima e
// acrescente a entrada correspondente aqui, na mesma chave kebab-case usada em
// `name="..."`.
// Marca do Claude (o "spark" laranja) — ícone dos artefatos do Claude na Área
// Interna (card em Interno.jsx e submenu "Artefatos" da NavBar). Não existe no
// lucide: raios em volta do centro, só contorno, sempre no laranja da marca
// (ignora `color`, que nos cards/menu viria com a cor do tema).
const CLAUDE_LARANJA = '#D97757';
const CLAUDE_RAIOS = [10, 8, 9.5, 7.5, 10, 8.5, 9, 7.5, 10, 8, 9.5, 8.5];
function ClaudeSpark({ size, strokeWidth, style, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={CLAUDE_LARANJA} strokeWidth={strokeWidth}
      strokeLinecap="round" style={style} className={className} aria-hidden="true">
      {CLAUDE_RAIOS.map((r, i) => {
        const a = (i * 30 - 90) * Math.PI / 180;
        return <line key={i} x1={12 + 2.5 * Math.cos(a)} y1={12 + 2.5 * Math.sin(a)} x2={12 + r * Math.cos(a)} y2={12 + r * Math.sin(a)}/>;
      })}
    </svg>
  );
}

const ICONS = {
  activity: Activity, 'alert-circle': AlertCircle, 'alert-triangle': AlertTriangle, archive: Archive, 'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight, 'arrow-up-down': ArrowUpDown, 'arrow-up-right': ArrowUpRight,
  'badge-check': BadgeCheck, banknote: Banknote, 'bar-chart-2': BarChart2, bell: Bell, 'bell-off': BellOff, 'building-2': Building2,
  calendar: Calendar, 'calendar-check': CalendarCheck, check: Check,
  'check-circle': CheckCircle, 'check-circle-2': CheckCircle2,
  'chevron-down': ChevronDown, 'chevron-left': ChevronLeft, 'chevron-right': ChevronRight,
  'chevron-up': ChevronUp, 'chevrons-down': ChevronsDown, 'chevrons-up': ChevronsUp,
  circle: Circle, 'circle-check': CircleCheck, 'circle-help': CircleHelp, claude: ClaudeSpark, 'clipboard-list': ClipboardList,
  clock: Clock, 'code-2': Code2, copy: Copy,
  database: Database, dot: Dot, download: Download,
  eye: Eye, 'eye-off': EyeOff, 'external-link': ExternalLink,
  'file-text': FileText, 'file-type-2': FileType2, 'flask-conical': FlaskConical, 'folder-open': FolderOpen,
  handshake: Handshake, hash: Hash, home: Home,
  info: Info,
  'key-round': KeyRound,
  landmark: Landmark, layers: Layers, 'layout-dashboard': LayoutDashboard,
  lightbulb: Lightbulb, 'line-chart': LineChart, 'link-2': Link2, 'list-filter': ListFilter,
  lock: Lock, 'log-out': LogOut, unlock: Unlock,
  mail: Mail, 'map-pin': MapPin, 'maximize-2': Maximize2, megaphone: Megaphone, menu: Menu,
  pencil: Pencil, 'piggy-bank': PiggyBank, play: Play, plus: Plus,
  'refresh-cw': RefreshCw, route: Route,
  'scroll-text': ScrollText, search: Search, 'search-check': SearchCheck, 'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck, 'sliders-horizontal': SlidersHorizontal, slash: Slash, square: Square, 'square-check': SquareCheck, star: Star,
  terminal: Terminal, 'trending-down': TrendingDown, 'trending-up': TrendingUp, 'trash-2': Trash2,
  upload: Upload,
  user: User, 'user-pen': UserPen, 'user-plus': UserPlus, 'user-x': UserX, users: Users,
  wallet: Wallet, wheat: Wheat,
  x: X, 'x-circle': XCircle,
};

// `fill` tem que ter default 'none' (não `undefined`) — o lucide-react já espera
// esse valor pra a maioria dos ícones (stroke-only); passar `undefined` explícito
// sobrescreve o `fill="none"` interno do componente e o SVG cai no preto padrão
// do próprio SVG (bug que pintou todo ícone do portal de preto).
export function LucideIcon({ name, size = 20, color = 'currentColor', strokeWidth = 2, fill = 'none', style, className }) {
  const Comp = ICONS[name];
  if (!Comp) return null;
  return <Comp size={size} color={color} strokeWidth={strokeWidth} fill={fill} style={style} className={className} />;
}

export default LucideIcon;
